import type { Ref, RefCallback } from 'react';

export function composeRefs<T>(...refs: (Ref<T> | null | undefined)[]): Ref<T> | null {
  if (refs.length === 2) {
    return composeTwoRefs(refs[0], refs[1]);
  }
  return (
    refs
      .slice(1)
      .reduce<Ref<T> | null | undefined>(
        (combined, ref) => composeTwoRefs(combined, ref),
        refs[0]
      ) ?? null
  );
}

function updateRef<T>(ref: NonNullable<Ref<T>>, instance: T | null) {
  if (typeof ref === 'function') {
    ref(instance);
  } else {
    (ref as { current: T | null }).current = instance;
  }
}

const composedRefCache = new WeakMap<
  NonNullable<Ref<unknown>>,
  WeakMap<NonNullable<Ref<unknown>>, RefCallback<unknown>>
>();

function composeTwoRefs<T>(
  ref1: Ref<T> | null | undefined,
  ref2: Ref<T> | null | undefined
): Ref<T> | null {
  if (ref1 && ref2) {
    const ref1Cache =
      composedRefCache.get(ref1 as NonNullable<Ref<unknown>>) ||
      new WeakMap<NonNullable<Ref<unknown>>, RefCallback<unknown>>();
    composedRefCache.set(ref1 as NonNullable<Ref<unknown>>, ref1Cache);
    const composedRef =
      (ref1Cache.get(ref2 as NonNullable<Ref<unknown>>) as RefCallback<T> | undefined) ||
      ((instance: T | null) => {
        updateRef(ref1, instance);
        updateRef(ref2, instance);
      });
    ref1Cache.set(ref2 as NonNullable<Ref<unknown>>, composedRef as RefCallback<unknown>);
    return composedRef;
  }
  if (!ref1) return (ref2 as Ref<T>) ?? null;
  return ref1;
}
